import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { CommonModule, NgClass, NgFor, NgIf } from '@angular/common';

import { NotificationService } from '../../../../Services/notification.service';
import { OrderCreationService } from '../../../../Services/order-creation.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { AuthService } from '../../../../Services/auth.service';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';

@Component({
  selector: 'app-raw-material',
  standalone: true,
  imports: [CommonModule, NgIf, NgFor, NgClass],
  templateUrl: './raw-material.component.html',
  styleUrls: ['./raw-material.component.css'],
})
export class RawMaterialComponent implements OnInit, OnDestroy {
  orders: any[] = [];
  isLoading: boolean = true;
  private subscriptions = new Subscription();

  constructor(
    private notificationService: NotificationService,
    private orderservice: OrderCreationService,
    private firmSelectionService: FirmSelectionService, // 1. Inject the service
    private authService:AuthService
  ) {}

  ngOnInit(): void {
    // const supplierId = localStorage.getItem('supplierId');
    const supplierId = this.authService.getUserId();
    if (!supplierId) {
      this.notificationService.showError('Supplier ID not found', 'Error');
      this.isLoading = false;
      return;
    }

    // 2. Subscribe to firm changes
    this.subscriptions.add(
      this.firmSelectionService.selectedFirmName$.subscribe(firm => {
        if (firm) {
          // Fetch data when a firm is selected
          this.getOrderList(supplierId, firm.id.toString());
        }
      })
    );

    // 3. Handle initial load
    const currentFirm = this.firmSelectionService.getselectedFirmName();
    if (currentFirm) {
      // Fetch data for the initially selected firm
      this.getOrderList(supplierId, currentFirm.id.toString());
    }
  }

  // 4. Update getOrderList to accept a firmId
  getOrderList(supplierId: string, firmId: string): void {
    this.isLoading = true; // Set loading state

    // Add both supplier_id and firm_id to the request parameters
    let params = new HttpParams()
      .set('supplier_id', supplierId);

    this.orderservice.getOrders(params).subscribe({
      next: (res: any) => {
        this.orders = (res.data || [])
          .filter((order: any) => order.status !== 'completed' &&  order.handover_status === 'handed_over')
          .map((order: any) => {
            const unifiedMetalDetails = this.unifyRawMaterialDetails(
              order.ordc_metal_details,
              order.ordc_submitted_metal_details,
              order.ordc_available_metal_details
            );

            const unifiedStoneDetails = this.unifyRawMaterialDetails(
              order.ordc_stone_details,
              order.ordc_submitted_stone_details,
              order.ordc_available_stone_details
            );

            return {
              ...order,
              unifiedMetalDetails,
              unifiedStoneDetails,
            };
          });

        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error fetching orders:', err);
        this.notificationService.showError('Failed to fetch orders', 'Error');
        this.isLoading = false;
      },
    });
  }

  /**
   * Combines assigned, submitted, and available data into a single array for easy display.
   */
  private unifyRawMaterialDetails(assigned: any, submitted: any, available: any): any[] {
    const assignedArray = Array.isArray(assigned) ? assigned : (assigned ? [assigned] : []);
    const submittedArray = Array.isArray(submitted) ? submitted : (submitted ? [submitted] : []);
    const availableArray = Array.isArray(available) ? available : (available ? [available] : []);

    return assignedArray.map(item => {
        const submittedItem = submittedArray.find(s => s.code === item.code) || {};
        const availableItem = availableArray.find(a => a.code === item.code) || {};

        return {
            // Base info
            code: item.code,
            artical_name: item.artical_name,
            stone_name: item.stone_name,
            quantity: item.quantity,

            // Aggregated values
            assigned_qty: item.calculated_product_qty || 0,
            assigned_weight: item.total_silver || item.total_stone || 0,
            submitted_qty: submittedItem.calculated_product_qty || 0,
            submitted_weight: submittedItem.total_silver || submittedItem.total_stone || 0,
            available_qty: availableItem.calculated_product_qty || 0,
            available_weight: availableItem.total_silver || availableItem.total_stone || 0,
        };
    });
  }

  // 5. Implement OnDestroy to clean up the subscription
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  
  requestReturn(orderId: number): void {
  this.orderservice.requestRawMaterialReturn(orderId).subscribe({
    next: () => {
      this.notificationService.showSuccess(
        'Return request sent to admin',
        'Success'
      );

      // Refresh list
      const firm = this.firmSelectionService.getselectedFirmName();
      if (firm) {
        this.getOrderList(
          this.authService.getUserId(),
          firm.id.toString()
        );
      }
    },
    error: err => {
      this.notificationService.showError(
        err.error?.message || 'Return failed',
        'Error'
      );
    }
  });
}

canShowReturn(order: any): boolean {
  return (
    order.request_status === 'raw_material_issue' &&
    order.status === 'issued'
  );
}
}
